import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RorosDomainService } from '@/services/RorosService';
import { rorosKeys } from './keys';
import type { UpdateDomainSettingsPayload } from '@/types/roros/domains';

const STALE_TIME = 2 * 60_000;
const GC_TIME = 5 * 60_000;

// ─── Queries ───────────────────────────────────────────────────────────────

export function useRorosDomains() {
  return useQuery({
    queryKey: rorosKeys.domains.all,
    queryFn: () => RorosDomainService.list(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosDomain(domainId: string) {
  return useQuery({
    queryKey: rorosKeys.domains.detail(domainId),
    queryFn: () => RorosDomainService.get(domainId),
    enabled: !!domainId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosDomainSettings(domainId: string) {
  return useQuery({
    queryKey: rorosKeys.domains.settings(domainId),
    queryFn: () => RorosDomainService.getSettings(domainId),
    enabled: !!domainId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

export function useRorosDomainActivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (domainId: string) => RorosDomainService.activate(domainId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.domains.all });
    },
  });
}

export function useRorosDomainDeactivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (domainId: string) => RorosDomainService.deactivate(domainId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.domains.all });
    },
  });
}

export function useRorosDomainUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, payload }: { domainId: string; payload: UpdateDomainSettingsPayload }) =>
      RorosDomainService.updateSettings(domainId, payload),
    onSuccess: (_data, { domainId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.domains.settings(domainId) });
      qc.invalidateQueries({ queryKey: rorosKeys.domains.detail(domainId) });
      // The domains list (DomainsPage) renders `domain.settings.isAdvisory`
      // to decide whether to show the Advisory badge.  Without invalidating
      // the list query the badge would stay stale until the next hard refresh
      // even though the DB write succeeded.
      qc.invalidateQueries({ queryKey: rorosKeys.domains.all });
    },
  });
}

/** M-Rhythm-1: set or clear the accountable actor for a domain. */
export function useRorosDomainSetAccountable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId, actorId }: { domainId: string; actorId: string | null }) =>
      RorosDomainService.setAccountable(domainId, actorId),
    onSuccess: (_data, { domainId }) => {
      // Invalidate both the domains list AND the domain settings query.
      // The settings invalidation advances `settingsQuery.dataUpdatedAt`, which is
      // used as depKey in DomainSettingsDialog's useFormSnapshot. This causes the
      // snapshot to reseed from the updated domain props, ensuring that a partial-save
      // scenario (accountable saved, settings failed) leaves the form correctly dirty
      // for only the settings fields on retry.
      qc.invalidateQueries({ queryKey: rorosKeys.domains.all });
      qc.invalidateQueries({ queryKey: rorosKeys.domains.settings(domainId) });
    },
  });
}
